import ExecutionEnvironment from 'exenv';
import type { Dispatch, Store } from 'redux';
import type { AddEventCallback, AmethystEvent, TitaniteConfig } from 'titanite-javascript';
import Titanite from 'titanite-javascript';

import { clearEventQueue, storeEventInQueue } from 'actions/amethyst';
import { trackError } from 'helpers/ErrorUtils';
import { logDebug, logError, logToServer } from 'middleware/logger';
import type { Marketplace } from 'types/app';

type WrapperEventFunction<T> = (eventData: T) => AmethystEvent;
type WrapperEventMethod<T> = () => [WrapperEventFunction<T> | undefined, T];

function logTitaniteNotInstantiatedError(callingFunctionName: string) {
  logError(`Attempt to call ${callingFunctionName} before titanite was instantiated`);
}

export function initTitaniteConfig(isMartyEnvProduction: boolean, marketplace: Marketplace): Partial<TitaniteConfig> {
  const stage = isMartyEnvProduction ? 'prod' : 'beta';
  const titaniteConfigFromMarketplaceConfig = marketplace.api.titanite;
  const amethystEnvConfigFromMarketplaceConfig = titaniteConfigFromMarketplaceConfig.amethystEnv;
  const marketplaceName = amethystEnvConfigFromMarketplaceConfig.marketplace as TitaniteConfig['amethystEnv']['marketplace'];
  return {
    ...titaniteConfigFromMarketplaceConfig,
    amethystEnv: {
      ...amethystEnvConfigFromMarketplaceConfig,
      marketplace: marketplaceName,
      stage
    }
  };
}

let titaniteInstance: Titanite | undefined;
let amethystStage: string | undefined;
let amethystMarketplace: string | undefined;

/**
 * Resolve the Amethyst collector host for the current marketplace/stage, or log
 * and return undefined if unknown. Shared by the direct-send helpers
 * (asyncTrack, trackOpsEvent) that bypass Titanite's transport.
 */
function getAmethystHost(): string | undefined {
  const hostMap: Record<string, string> = {
    'zappos:beta': 'beta.amethyst.zappos.com',
    'zappos:prod': 'amethyst.zappos.com',
    '6pm:beta': 'beta.amethyst.6pm.com',
    '6pm:prod': 'amethyst.6pm.com'
  };
  const host = hostMap[`${amethystMarketplace}:${amethystStage}`];
  if (!host) {
    logError(`Unknown Amethyst host for ${amethystMarketplace}:${amethystStage}`);
  }
  return host;
}

export function initTitanite(titaniteConfig: TitaniteConfig, zfcSessionId?: string, directedId?: string) {
  titaniteInstance = new Titanite(titaniteConfig);
  amethystStage = titaniteConfig.amethystEnv?.stage;
  amethystMarketplace = titaniteConfig.amethystEnv?.marketplace;

  if (ExecutionEnvironment.canUseDOM) {
    window.titanite = titaniteInstance;
  } else {
    logError('Attempting to initialize titanite server side');
  }

  if (zfcSessionId) {
    titaniteInstance.setDefaultFieldValue('sessionId', zfcSessionId);
  } else if (ExecutionEnvironment.canUseDOM && window.zfcSessionId) {
    titaniteInstance.setDefaultFieldValue('sessionId', window.zfcSessionId);
  }

  if (directedId) {
    titaniteInstance.setDefaultFieldValue('directedCustomerId', directedId);
  }
}

const isTestEnv = process.env.NODE_ENV === 'test';

// WebsiteEvent.session_id is required, so Amethyst rejects any event sent without one. This happens
// on hosts with no ZFC session, e.g. automated traffic on policy.live.us-east-1.marty.zappos.com.
function hasSessionId(payload: object, titanite: Titanite): boolean {
  return Boolean((payload as { sessionId?: string }).sessionId || titanite.defaultFieldValues?.sessionId);
}

function logSessionlessEventDropped(payload: object) {
  logToServer(
    `[AMETHYST][marty] website event omitted: empty sessionId` +
      ` | event=${Object.keys(payload)[0] ?? 'unknown'}` +
      ` | path=${typeof window !== 'undefined' ? window.location?.pathname : ''}` +
      ` | ua=${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`
  );
}

let queuedAmethystEvents: AmethystEvent[] = [];

const addEventCallback: AddEventCallback = r => {
  const failed = !r.success || (r.failedEvents && r.failedEvents.length);
  if (failed) {
    return trackError('NON-FATAL', 'Track Amethyst Event: Titanite FAILED', JSON.stringify(r));
  } else {
    logDebug('Track Amethyst Event: Titanite SUCCESS', JSON.stringify(r));
  }
};

interface TrackWithAmethystArgs<T> {
  canUseDOM: boolean;
  dispatch?: Dispatch;
  eventData: T;
  eventFunction?: WrapperEventFunction<T>;
  overrideDebounce?: boolean;
  titanite: Titanite | undefined;
}

function trackWithAmethyst<T>(args: TrackWithAmethystArgs<T>) {
  const { canUseDOM, dispatch, eventData, eventFunction, titanite, overrideDebounce } = args;

  let payload;

  // if event is not passed in
  if (!eventData) {
    logDebug('Please pass in an amethyst event');
    return;
  }

  // if using middleware ( 2 params vs original 3 )
  if (!eventFunction) {
    payload = { ...eventData };
  } else {
    payload = { ...eventFunction(eventData) };
  }

  if (!canUseDOM) {
    if (!dispatch) {
      // log the keys of the payload since they won't contain PII and will help track down when/where this is coming from
      const fields = Object.keys(payload).filter(val => val !== 'abTests');
      logError(`Use serverTrack if attempting to fire an event server-side, payload fields were [${fields.join()}]`);
      return;
    }
    dispatch(storeEventInQueue(payload));
  } else if (!hasView) {
    // Queue events that have been fired before titaniteView() has been initiated.
    // Once we have a view then fire the queued events.
    queuedAmethystEvents.push(payload);
    return;
  } else {
    if (!titanite) {
      logTitaniteNotInstantiatedError('trackWithAmethyst');
      return;
    }
    if (!hasSessionId(payload, titanite)) {
      logSessionlessEventDropped(payload);
      return;
    }
    // dispatch amethyst event
    titanite.addEvent(payload, addEventCallback, overrideDebounce);
  }
}

export const flushServerSideQueue = (store: Store, titanite = titaniteInstance) => {
  if (!titanite) {
    logTitaniteNotInstantiatedError('flushServerSideQueue');
    return;
  }
  const {
    amethyst: { queue }
  } = store.getState();
  if (queue.length) {
    const sendable = queue.filter((payload: AmethystEvent) => {
      if (hasSessionId(payload, titanite)) {
        return true;
      }
      logSessionlessEventDropped(payload);
      return false;
    });
    if (sendable.length) {
      titanite.addEvents(sendable, addEventCallback);
    }
    store.dispatch(clearEventQueue());
  }
};

const fireQueuedAmethystEvents = (titanite = titaniteInstance) => {
  if (!titanite) {
    logTitaniteNotInstantiatedError('fireQueuedAmethystEvents');
    return;
  }
  if (queuedAmethystEvents.length) {
    queuedAmethystEvents.forEach(payload => {
      if (!hasSessionId(payload, titanite)) {
        logSessionlessEventDropped(payload);
        return;
      }
      titanite.addEvent(payload, addEventCallback);
    });
    queuedAmethystEvents = [];
  }
};

let hasView = false;
const trackViewWithTitanite = (titanite: Titanite) => {
  if (hasView) {
    titanite.endView();
  }

  hasView = true;
  titanite.startView();

  fireQueuedAmethystEvents(titanite);
};

/**
 * tracking amethyst event Server-Side
 * @param  {Function} eventMethod     function that returns an array that contains the event function and object
 *                                    track(() => ([eventFunction, eventObject]))
 * @param  {Function} dispatch
 */
export function serverTrack<T>(eventMethod: WrapperEventMethod<T>) {
  return (dispatch: Dispatch) => track(eventMethod, dispatch);
}

function trackHelper<T>(
  eventMethod: WrapperEventMethod<T>,
  dispatch?: Dispatch,
  canUseDOM = ExecutionEnvironment.canUseDOM,
  titanite = titaniteInstance,
  overrideTestEnv = false,
  overrideDebounce = false,
  sendTrackError = trackError
) {
  try {
    const [eventFunction, eventData] = eventMethod();
    if (eventFunction) {
      (!isTestEnv || overrideTestEnv) &&
        trackWithAmethyst({
          eventFunction,
          eventData,
          titanite,
          dispatch,
          overrideDebounce,
          canUseDOM
        });
    }
  } catch (err: any) {
    sendTrackError('NON-FATAL', 'Amethyst data error.', err);
  }
}

/**
 * tracking amethyst event
 * @param  {Function} eventMethod     function that returns an array that contains the event function and object
 *                                    track(() => ([eventFunction, eventObject]))
 * @param  {Boolean} dispatch         needed if event is triggered server-side
 * All params below are for mocking and testing.
 * @param  {Boolean} canUseDOM        is the DOM available?
 * @param  {Object} titanite          Is the instance of titanite from the library?
 * @param  {Boolean} overrideTestEnv  Are we faking the test environment?
 * @param  {Function} sendTrackError  for tracking errors
 */
export function track(
  eventMethod: (...args: any[]) => any,
  dispatch?: Dispatch,
  canUseDOM = ExecutionEnvironment.canUseDOM,
  titanite = titaniteInstance,
  overrideTestEnv = false,
  overrideDebounce = false,
  sendTrackError = trackError
) {
  trackHelper(eventMethod, dispatch, canUseDOM, titanite, overrideTestEnv, overrideDebounce, sendTrackError);
}

// new fire event middleware
export function middlewareTrack<T>(eventData: T, canUseDOM = ExecutionEnvironment.canUseDOM, titanite = titaniteInstance, overrideTestEnv = false) {
  return (!isTestEnv || overrideTestEnv) && trackWithAmethyst({ eventData, titanite, canUseDOM });
}

/**
 * Fire an Amethyst website event to the collector via a `keepalive` fetch, which
 * the browser allows to finish even after the page unloads. This is what lets the
 * event survive the cross-origin proceed-to-checkout navigation (Marty -> Kratos)
 * that would abort an ordinary in-flight request.
 *
 * The send is awaited against a safety timeout: a fast send (the common case,
 * ~200ms median) resolves and confirms delivery in-page before we navigate; a
 * slower send is not waited on past `safetyTimeoutMs` — keepalive guarantees it
 * completes after unload, so the checkout click never blocks longer than that.
 *
 * Why not use Titanite's addEvent here: even with the debounce bypassed, Titanite
 * sends via sendBeacon-or-XHR and the event is lost unless the page is kept
 * alive. (FEE-8812)
 */
export async function asyncTrack<T extends AmethystEvent>(
  eventData: T,
  { canUseDOM = ExecutionEnvironment.canUseDOM, titanite = titaniteInstance, overrideTestEnv = false, safetyTimeoutMs = 1000 } = {}
): Promise<void> {
  if (isTestEnv && !overrideTestEnv) {
    return;
  }
  if (!canUseDOM || !titanite) {
    if (!titanite) {
      logTitaniteNotInstantiatedError('asyncTrack');
    }
    return;
  }

  const host = getAmethystHost();
  if (!host) {
    return;
  }

  const win = typeof window === 'object' ? window : undefined;

  // Replicate the device dimensions Titanite's addEvent appends.
  const deviceDimensions: Record<string, number> = {};
  if (win?.innerWidth) {
    deviceDimensions.viewportWidth = win.innerWidth;
    deviceDimensions.viewportHeight = win.innerHeight;
  }
  if (win?.screen?.width) {
    deviceDimensions.screenWidth = win.screen.width;
    deviceDimensions.screenHeight = win.screen.height;
  }

  // Replicate viewId: Titanite ties events to the current view via
  // sessionViewIds[sessionId] (set by startView).
  const sessionId = (eventData as { sessionId?: string }).sessionId || (titanite.defaultFieldValues?.sessionId as string | undefined);
  if (!sessionId) {
    logSessionlessEventDropped(eventData);
    return;
  }
  const viewId = titanite.sessionViewIds?.[sessionId];

  // Event fields first so consumers that key off the first field (e.g. the
  // zfc-decoder's Object.keys(websiteEvent)[0]) read the event name, matching
  // the order Titanite's addEvent produces.
  const websiteEvent: Record<string, unknown> = { ...eventData };
  // Backfill the default fields Titanite normally appends in addEvent, without
  // overriding anything the event already set.
  for (const [fieldName, value] of Object.entries(titanite.defaultFieldValues ?? {})) {
    if (!Object.prototype.hasOwnProperty.call(websiteEvent, fieldName)) {
      websiteEvent[fieldName] = value;
    }
  }
  // Per-event context, matching what addEvent would have produced.
  websiteEvent.deviceDimensions = deviceDimensions;
  if (viewId) {
    websiteEvent.viewId = viewId;
  }
  websiteEvent.referrer = titanite.referrer ?? win?.document?.referrer ?? '';
  websiteEvent.timestamp = Date.now();
  if (win?.location) {
    websiteEvent.urlHost = win.location.hostname;
    websiteEvent.urlPath = win.location.pathname;
    websiteEvent.urlQuery = win.location.search;
    websiteEvent.urlFragment = win.location.hash;
  }

  const url = `https://${host}:443/api/v1/events?X-Amethyst-Location=` + encodeURIComponent(win?.location?.href || 'unavailable');
  const body = JSON.stringify({ events: [{ websiteEvent }] });

  // The event name is the key in eventData (same convention the decoder uses),
  // not a dedicated eventTypeId field.
  const eventTypeId = Object.keys(eventData)[0] ?? 'unknown';

  // keepalive lets the request outlive the page unload that follows the proceed-to-checkout
  // navigation. The proceed payload is far under keepalive's 64KB cap.
  const send = fetch(url, { body, credentials: 'include', method: 'POST', keepalive: true })
    .then(() => {
      logDebug(`[AMETHYST] asyncTrack delivered in-page: ${eventTypeId}`);
    })
    .catch(e => {
      trackError('NON-FATAL', 'asyncTrack fetch failed', e);
    });

  // Cap how long the click blocks. If the send is slower than this we stop awaiting and let
  // keepalive finish it after unload; the server log tells us how often we fall back to that.
  let timeoutId: ReturnType<typeof setTimeout>;
  const safety = new Promise<void>(resolve => {
    timeoutId = setTimeout(() => {
      logToServer(`[AMETHYST] asyncTrack safety timeout after ${safetyTimeoutMs}ms; relying on keepalive to finish: ${eventTypeId}`);
      resolve();
    }, safetyTimeoutMs);
  });

  await Promise.race([send, safety]);
  clearTimeout(timeoutId!);
}

// fire start/end view
export function titaniteView(canUseDOM = ExecutionEnvironment.canUseDOM, titanite = titaniteInstance) {
  if (!canUseDOM) {
    return;
  }
  if (!titanite) {
    logTitaniteNotInstantiatedError('titaniteView');
    return;
  }
  trackViewWithTitanite(titanite);
}
/**
 * Send an ops event directly to the Amethyst API using the opsEvent envelope.
 *
 * Bypasses titanite's addEvent (which always wraps in websiteEvent) but reuses
 * the same Amethyst endpoint derived from titanite's config. This ensures ops
 * events are routed correctly through the metrics pipeline.
 */
export function trackOpsEvent(opsEventPayload: Record<string, unknown>, canUseDOM = ExecutionEnvironment.canUseDOM, overrideTestEnv = false) {
  if (!canUseDOM || (isTestEnv && !overrideTestEnv)) {
    return;
  }

  if (!titaniteInstance) {
    logTitaniteNotInstantiatedError('trackOpsEvent');
    return;
  }

  const host = getAmethystHost();
  if (!host) {
    return;
  }

  const sessionId = opsEventPayload.sessionId || titaniteInstance.defaultFieldValues?.sessionId || '';

  // OpsEvent.session_id is a required, non-empty proto field. In contexts with no ZFC
  // session (headless/synthetic browsers, internal-tool embeds of the shared header/footer)
  // it resolves empty and Amethyst rejects the event outright — omit rather than emit a
  // guaranteed failure.
  if (!sessionId) {
    // Log the dropped-event context so we can diagnose why ops events lack a session. Tagged
    // `marty` to distinguish it from the equivalent kratos path in the shared Amethyst dashboards.
    logToServer(
      `[AMETHYST][marty] ops event omitted: empty sessionId` +
        ` | eventTypeId=${opsEventPayload.eventTypeId ?? 'unknown'}` +
        ` | path=${typeof window !== 'undefined' ? window.location?.pathname : ''}` +
        ` | referrer=${typeof document !== 'undefined' ? document.referrer : ''}` +
        ` | ua=${typeof navigator !== 'undefined' ? navigator.userAgent : ''}`
    );
    return;
  }

  const urlPath = typeof window !== 'undefined' ? window.location?.pathname : '';

  const envelope = {
    events: [
      {
        opsEvent: {
          ...opsEventPayload,
          sessionId,
          urlPath: opsEventPayload.urlPath || urlPath
        }
      }
    ]
  };

  const url = `https://${host}:443/api/v1/events`;
  const body = JSON.stringify(envelope);

  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(
        url + '?X-Amethyst-Location=' + encodeURIComponent(window.location?.href || ''),
        new Blob([body], { type: 'text/plain' })
      );
    } else {
      logDebug('sendBeacon unavailable for ops event, falling back to fetch');
      fetch(url, { body, credentials: 'include', method: 'POST' });
    }
  } catch (err: any) {
    trackError('NON-FATAL', 'Failed to send ops event', err);
  }
}
